import React from 'react';
import {SpanHighlight} from './PdfViewer.types';
import {TagHighlight} from './tagHighlights';
import {isPositiveFinite, PageViewBox, PctBox, spanToBoxPct, tagHighlightArea} from './spanMath';
import {Span} from '../../search/search-types';

interface Props {
    tagHighlights: TagHighlight[];
    queryHighlights: SpanHighlight[];
    page: number;
    rotate: number; // absolute page rotation
    pageView: PageViewBox;
    showTags: boolean;
}

/**
 * Dimensions to position a tag span against. Its own when usable, otherwise pdf.js's for the page,
 * unrotated, which is the frame `spanToBoxPct` expects.
 *
 * Null when neither source works, which skips the box; the panel row stays either way.
 */
function positioningDims(span: Span, pageView: PageViewBox): { w: number; h: number } | null {
    const w = isPositiveFinite(span.pageWidth) ? span.pageWidth : pageView.w;
    const h = isPositiveFinite(span.pageHeight) ? span.pageHeight : pageView.h;
    return isPositiveFinite(w) && isPositiveFinite(h) ? {w, h} : null;
}

const style = (b: PctBox): React.CSSProperties => ({
    left: `${b.leftPct}%`,
    top: `${b.topPct}%`,
    width: `${b.widthPct}%`,
    height: `${b.heightPct}%`,
});

/**
 * Client-side highlight layer drawn over the pdf.js canvas (positioned as % of the page, so it scales/pans
 * with react-zoom-pan-pinch and stays aligned under zoom/rotation). Query hits are drawn from their spans,
 * tags from their spans grown by `tagHighlightArea`. Each box carries `data-annotation-id`, which the
 * `scrollToHighlight` navigation targets.
 */
export function HighlightOverlay({tagHighlights, queryHighlights, page, rotate, pageView, showTags}: Props) {
    return (
        <div className="hibu-highlight-overlay">
            {showTags &&
                tagHighlights
                    .filter((t) => t.page === page)
                    .map((t) => ({highlight: t, dims: positioningDims(t.span, pageView)}))
                    .filter((t): t is { highlight: TagHighlight; dims: { w: number; h: number } } => t.dims !== null)
                    .map(({highlight, dims}) => (
                        <div
                            key={highlight.id}
                            data-annotation-id={highlight.id}
                            className="hibu-tag-highlight"
                            title={highlight.titleObj.str}
                            style={style(spanToBoxPct(
                                tagHighlightArea({...highlight.span, pageWidth: dims.w, pageHeight: dims.h}), rotate,
                            ))}
                        />
                    ))}
            {queryHighlights
                .filter((q) => q.page === page)
                .map((q) => ({highlight: q, dims: positioningDims(q.span, pageView)}))
                .filter((q): q is { highlight: SpanHighlight; dims: { w: number; h: number } } => q.dims !== null)
                .map(({highlight, dims}) => (
                    <div
                        key={highlight.id}
                        data-annotation-id={highlight.id}
                        className="hibu-query-highlight"
                        style={style(spanToBoxPct({...highlight.span, pageWidth: dims.w, pageHeight: dims.h}, rotate))}
                    />
                ))}
        </div>
    );
}
